'use client';

import { Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useLayoutEffect, useSyncExternalStore } from 'react';
import { Switch } from '@/components/ui/switch';
import {
  MARKETING_DEV_THEME_STORAGE_KEY,
  planDevThemeSwitch,
  type MarketingColorMode,
} from '@/components/marketing-landing/marketing-dev-theme';

function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
  return () => observer.disconnect();
}

function readDocumentMode(): MarketingColorMode {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

function clearMarketingColorOverride(): void {
  document
    .querySelector('[data-marketing-theme]')
    ?.removeAttribute('data-marketing-color');
  try {
    sessionStorage.removeItem(MARKETING_DEV_THEME_STORAGE_KEY);
  } catch {
    // Private mode still gets the document-theme flip.
  }
}

export function DevToolsThemeSwitch() {
  const { setTheme } = useTheme();
  const mode = useSyncExternalStore(
    subscribe,
    readDocumentMode,
    (): MarketingColorMode => 'light'
  );
  const next = planDevThemeSwitch(mode);

  useLayoutEffect(() => {
    clearMarketingColorOverride();
  }, []);

  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-2 text-sm">
        {mode === 'dark' ? (
          <Moon className="size-4" aria-hidden="true" />
        ) : (
          <Sun className="size-4" aria-hidden="true" />
        )}
        Theme
      </span>
      <Switch
        checked={mode === 'light'}
        aria-label={
          next.documentTheme === 'dark' ? 'Switch to dark' : 'Switch to light'
        }
        onCheckedChange={() => {
          setTheme(next.documentTheme);
          clearMarketingColorOverride();
        }}
      />
    </div>
  );
}
